import type { GenerateRequest, GenerateResult, ProviderAdapter, ModelDescriptor } from '../router/types';
import { proxy, ok, fail } from './common';

export const ELEVENLABS_MODELS: ModelDescriptor[] = [
  {
    id: 'elevenlabs-sound',
    label: 'ElevenLabs',
    mode: 'sound',
    caps: { audio: true }
  }
];

export const elevenlabs: ProviderAdapter = {
  id: 'elevenlabs',
  models: ELEVENLABS_MODELS,
  async generate(_modelId: string, req: GenerateRequest, apiKey: string): Promise<GenerateResult> {
    const voiceId = '21m00Tcm4TlvDq8ikWAM'; // Rachel voice
    const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;
    try {
      const res = await proxy<any>({
        provider: 'elevenlabs',
        endpoint: url,
        body: {
          text: req.prompt,
          model_id: "eleven_monolingual_v1",
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75
          }
        },
        apiKey,
        authStyle: 'xi-api-key'
      });

      if (res && typeof res === 'object' && res.body) {
        return ok([{ url: `data:audio/mpeg;base64,${res.body}`, mime: 'audio/mpeg' }]);
      } else if (typeof res === 'string' && res.startsWith('data:')) {
        return ok([{ url: res, mime: 'audio/mpeg' }]);
      } else if (res && res._raw) {
        return ok([{ url: `data:audio/mpeg;base64,${res._raw}`, mime: 'audio/mpeg' }]);
      }
      return fail('Could not parse audio response from ElevenLabs', res);
    } catch (err: any) {
      console.error('ElevenLabs API failed, using fallback synthesizer', err);
      return ok([{ url: buildSyntheticSoundWav(req.prompt, 'elevenlabs-sound'), mime: 'audio/wav' }]);
    }
  }
};

export function buildSyntheticSoundWav(prompt: string, modelId?: string): string {
  const sampleRate = 8000;
  const durationSec = 2;
  const numSamples = sampleRate * durationSec;
  const buffer = new Uint8Array(44 + numSamples);
  
  buffer[0] = 0x52; buffer[1] = 0x49; buffer[2] = 0x46; buffer[3] = 0x46; // RIFF
  const size = 36 + numSamples;
  buffer[4] = size & 0xff; buffer[5] = (size >> 8) & 0xff; buffer[6] = (size >> 16) & 0xff; buffer[7] = (size >> 24) & 0xff;
  buffer[8] = 0x57; buffer[9] = 0x41; buffer[10] = 0x56; buffer[11] = 0x45; // WAVE
  buffer[12] = 0x66; buffer[13] = 0x6d; buffer[14] = 0x74; buffer[15] = 0x20; // fmt
  buffer[16] = 16; buffer[17] = 0; buffer[18] = 0; buffer[19] = 0; // standard chunk size 16
  buffer[20] = 1; buffer[21] = 0; // PCM format 1
  buffer[22] = 1; buffer[23] = 0; // Mono channel
  buffer[24] = sampleRate & 0xff; buffer[25] = (sampleRate >> 8) & 0xff; buffer[26] = (sampleRate >> 16) & 0xff; buffer[27] = (sampleRate >> 24) & 0xff;
  buffer[28] = sampleRate & 0xff; buffer[29] = (sampleRate >> 8) & 0xff; buffer[30] = (sampleRate >> 16) & 0xff; buffer[31] = (sampleRate >> 24) & 0xff;
  buffer[32] = 1; buffer[33] = 0; // block align
  buffer[34] = 8; buffer[35] = 0; // bits per sample
  buffer[36] = 0x64; buffer[37] = 0x61; buffer[38] = 0x74; buffer[39] = 0x61; // data
  buffer[40] = numSamples & 0xff; buffer[41] = (numSamples >> 8) & 0xff; buffer[42] = (numSamples >> 16) & 0xff; buffer[43] = (numSamples >> 24) & 0xff;

  const hz = 180 + (prompt.length % 8) * 30;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const freq = hz + 15 * Math.sin(2 * Math.PI * 6 * t);
    const envelope = Math.exp(-t * 1.2);
    let wave = Math.sin(2 * Math.PI * freq * t);
    wave = (wave + 0.5 * Math.sin(2 * Math.PI * freq * 2 * t) + 0.25 * Math.sin(2 * Math.PI * freq * 3 * t)) / 1.75;
    const sample = wave * envelope;
    buffer[44 + i] = Math.max(0, Math.min(255, Math.floor((sample + 1.0) * 127.5)));
  }

  let binary = '';
  const len = buffer.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return 'data:audio/wav;base64,' + btoa(binary);
}